<template>
    <BCard class="document-information-guided-section mb-1">
        <template #header>
            <div>
                <h5 class="mb-25">
                    {{ translate('Component(ResearchReviewProcess):document-information', 'Document information') }}
                </h5>
                <p class="text-less-muted mb-0">
                    {{ translate(
                        'Component(ResearchReviewProcess):document-information-description',
                        'Confirm the document type, relevance and applicability.',
                    ) }}
                </p>
            </div>
        </template>

        <BRow class="document-information-guided-section__primary-fields">
            <BCol
                cols="12"
                :md="showRelevance ? 4 : 6"
            >
                <BFormGroup :label="translate('Component(Grid):column-type', 'Document type')">
                    <VSelect
                        :value="selectedOption(documentTypeOptions, documentInformation.type)"
                        :options="documentTypeOptions"
                        label="label"
                        :clearable="false"
                        :append-to-body="true"
                        @input="updateDocumentInformation('type', $event ? $event.id : null)"
                    />
                </BFormGroup>
            </BCol>
            <BCol
                cols="12"
                :md="showRelevance ? 4 : 6"
            >
                <BFormGroup :label="translate('Component(Label):date', 'Document date')">
                    <BFormInput
                        type="date"
                        :value="documentInformation.date"
                        @input="updateDocumentInformation('date', $event || null)"
                    />
                </BFormGroup>
            </BCol>
            <BCol
                v-if="showRelevance"
                cols="12"
                md="4"
            >
                <BFormGroup :label="translate('Component(Grid):column-relevance', 'Relevance')">
                    <VSelect
                        :value="selectedOption(relevanceOptions, documentInformation.relevance)"
                        :options="relevanceOptions"
                        label="label"
                        :clearable="false"
                        :append-to-body="true"
                        @input="updateDocumentInformation('relevance', $event ? $event.id : null)"
                    />
                </BFormGroup>
            </BCol>
            <BCol cols="12">
                <BFormGroup
                    :label="translate('Component(Label):scope', 'Applicability')"
                    class="mb-0"
                >
                    <div
                        class="document-information-guided-section__scope-options"
                        :class="{
                            'document-information-guided-section__scope-options--without-unknown': !showUnknownScope,
                        }"
                    >
                        <BFormRadio
                            v-for="option in scopeOptions"
                            :key="option.id"
                            :checked="classification.scope"
                            :value="option.id"
                            name="guided-document-review-scope"
                            class="document-information-guided-section__scope-option"
                            :class="{
                                'document-information-guided-section__scope-option--selected': classification.scope === option.id,
                            }"
                            @click.native="selectScope(option.id)"
                            @change="selectScope($event)"
                        >
                            <strong>{{ option.label }}</strong>
                            <BBadge
                                v-if="isSuggestedScope(option.id)"
                                variant="light-primary"
                                class="ml-50"
                            >
                                {{ translate('Component(ResearchReviewSuggestion):suggested', 'Suggested') }}
                            </BBadge>
                        </BFormRadio>
                    </div>
                    <small
                        v-if="scopeSuggestion"
                        class="text-less-muted d-block mt-50"
                    >
                        {{ translate(scopeSuggestion.reasonKey, scopeSuggestion.fallbackReason) }}
                    </small>
                </BFormGroup>
            </BCol>
        </BRow>
    </BCard>
</template>

<script>
import {
    BBadge,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BRow,
} from 'bootstrap-vue'
import VSelect from 'vue-select'
import { VerificationDocumentType } from '@/libs/product'

export default {
    name: 'GuidedDocumentInformationSection',
    components: {
        BBadge,
        BCard,
        BCol,
        BFormGroup,
        BFormInput,
        BFormRadio,
        BRow,
        VSelect,
    },
    props: {
        classification: {
            type: Object,
            required: true,
        },
        documentInformation: {
            type: Object,
            required: true,
        },
        suggestions: {
            type: Array,
            default: () => [],
        },
        showUnknownScope: {
            type: Boolean,
            default: true,
        },
        showRelevance: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        documentTypeOptions() {
            return ['other', ...VerificationDocumentType.all()].map(type => ({
                id: type,
                label: this.translate(
                    VerificationDocumentType.translationKey(type),
                    type.replace(/_/g, ' '),
                ),
            }))
        },
        relevanceOptions() {
            return ['relevant', 'uncertain', 'irrelevant'].map(value => ({
                id: value,
                label: this.translate(
                    `Component(ResearchReviewProcessEnum):relevance-${value}`,
                    value.replace(/_/g, ' '),
                ),
            }))
        },
        scopeOptions() {
            const values = ['product_specific', 'global_supplier', 'unknown']

            return values.filter(value => this.showUnknownScope || value !== 'unknown').map(value => ({
                id: value,
                label: this.translate(
                    `Component(ResearchReviewProcessEnum):scope-${value}`,
                    value.replace(/_/g, ' '),
                ),
            }))
        },
        scopeSuggestion() {
            return this.suggestions.find(suggestion => (
                suggestion && suggestion.path === 'classification.scope'
            )) || null
        },
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        selectedOption(options, value) {
            return options.find(option => option.id === value) || null
        },
        isSuggestedScope(value) {
            return !!this.scopeSuggestion && this.scopeSuggestion.value === value
        },
        updateClassification(key, value) {
            this.$emit('update:classification', {
                ...this.classification,
                [key]: value,
            })
        },
        selectScope(value) {
            if (this.classification.scope === value) {
                return
            }

            this.updateClassification('scope', value)
        },
        updateDocumentInformation(key, value) {
            this.$emit('update:document-information', {
                ...this.documentInformation,
                [key]: value,
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.document-information-guided-section__scope-options {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: .75rem;
}

.document-information-guided-section__scope-options--without-unknown {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.document-information-guided-section__scope-option {
    min-height: 3rem;
    margin: 0;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    cursor: pointer;
    transition: border-color .15s ease, background-color .15s ease;

    &.custom-radio.custom-control {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: .65rem 1rem;
    }

    &--selected {
        border-color: #f58049;
        background: rgba(245, 128, 73, .04);
    }

    ::v-deep .custom-control-label {
        display: inline-flex;
        align-items: center;
        width: auto;
        min-height: 1.3rem;
        padding-left: 2.5rem;
        cursor: pointer;
    }

    ::v-deep .custom-control-label::before,
    ::v-deep .custom-control-label::after {
        top: 50%;
        left: 0;
        margin-left: .75rem;
        transform: translateY(-50%);
    }
}

@media (max-width: 767.98px) {
    .document-information-guided-section__scope-options,
    .document-information-guided-section__scope-options--without-unknown {
        grid-template-columns: 1fr;
    }
}
</style>
